import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Cell,
} from "recharts";
import { summaryCardTheme as t } from "./styles";

const SEVERITY_ORDER = ["CRITICAL", "HIGH", "MEDIUM", "LOW"];

function SummarySeverityChart({ securityFailedArnsBySeverity, onSeverityClick, activeSeverity = "" }) {
  const data = useMemo(() => {
    const bySeverity = securityFailedArnsBySeverity || {};
    const total = Object.values(bySeverity).reduce((a, b) => a + (Number(b) || 0), 0) || 1;
    return SEVERITY_ORDER.filter((s) => (Number(bySeverity[s]) || 0) > 0).map(
      (name) => ({
        name,
        value: Number(bySeverity[name]) || 0,
        fill: t.colors.severity[name] || t.colors.chart[3],
        pct: total ? Math.round(((Number(bySeverity[name]) || 0) / total) * 100) : 0,
      })
    );
  }, [securityFailedArnsBySeverity]);

  const totalArns = data.reduce((a, d) => a + d.value, 0);

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    return (
      <Box
        sx={{
          ...t.chartTooltip,
        }}
      >
        <Typography component="span" sx={{ fontWeight: 600, color: t.colors.text, fontSize: "0.75rem" }}>
          {d.name}
        </Typography>
        <Typography component="span" sx={{ color: t.colors.textMuted, fontSize: "0.75rem", ml: 0.5 }}>
          — {d.value} ARNs ({d.pct}%)
        </Typography>
      </Box>
    );
  };

  if (data.length === 0) {
    return (
      <Box
        sx={{
          height: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: t.radius.lg,
          border: `1px solid ${t.colors.border}`,
          backgroundColor: t.colors.card,
        }}
      >
        <Typography variant="body2" sx={{ color: t.colors.textMuted }}>
          No security findings by severity (ARNs)
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: t.colors.card,
        border: `1px solid ${t.colors.border}`,
        borderRadius: t.radius.lg,
        p: 2,
        boxShadow: t.shadow,
        height: "100%",
        minHeight: 280,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: t.colors.text, fontSize: "0.875rem", mb: 1, flexShrink: 0 }}>
        Security
      </Typography>
      
      <Box sx={{ flex: 1, minHeight: 200, width: "100%", position: "relative" }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 4, right: 16, left: 8, bottom: 28 }}
            barCategoryGap={data.length <= 3 ? "12%" : "8%"}
            onClick={(state) => {
              if (!onSeverityClick || !state?.activePayload?.length) return;
              const sev = state.activePayload[0]?.payload?.name;
              if (sev) onSeverityClick(sev);
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={t.colors.borderLight} horizontal={false} />
            <XAxis
              type="number"
              tick={{ fontSize: 11, fill: t.colors.textMuted }}
              label={{
                value: "Failed ARNs",
                position: "bottom",
                offset: 4,
                style: { fill: t.colors.textMuted, fontSize: 11, fontWeight: 500, fontFamily: "system-ui, sans-serif" },
              }}
            />
            <YAxis type="category" dataKey="name" width={56} tick={{ fontSize: 11, fill: t.colors.text }} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: t.colors.background }} />
            <Bar
              dataKey="value"
              nameKey="name"
              radius={[0, 4, 4, 0]}
              maxBarSize={data.length <= 3 ? 32 : 24}
              cursor={onSeverityClick ? "pointer" : "default"}
            >
              {data.map((entry) => {
                const hasSel = Boolean(activeSeverity);
                const selected = hasSel && entry.name === activeSeverity;
                return (
                  <Cell
                    key={entry.name}
                    fill={entry.fill}
                    fillOpacity={hasSel ? (selected ? 1 : 0.2) : 1}
                    stroke={selected ? t.colors.accent : "transparent"}
                    strokeWidth={selected ? 2.5 : 0}
                    style={
                      hasSel
                        ? selected
                          ? { filter: "drop-shadow(0 0 10px rgba(59,130,246,0.5))" }
                          : { filter: "blur(1.2px)" }
                        : undefined
                    }
                  />
                );
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

export default SummarySeverityChart;
